import classnames from 'classnames'
import { format, isToday } from 'date-fns'
import * as React from 'react'

import { withQueryClientContext } from '../../../../../app/shared/components/QueryClientContext'
import { Icon, useDialogState } from '../../../../components'
import { WithTooltip } from '../../../../components/WithTooltip'
import { parseRouteInitData } from '../../../../util-routing'
import { QuoteContext } from '../../QuoteContext'
import { FeedbackDialog } from './Dialog'
import { LikeButton } from './LikeButton'
import { ToggleButton } from './ToggleButton'
import { WhyStockMovingInitData } from './types'
import { useRating } from './useRating'
import { useWhyStockMoving } from './useWhyStockMoving'

const SENTIMENT_COLORS_CLASSNAMES: Record<string, string> = {
  good: 'text-positive',
  bad: 'text-negative',
  neutral: 'text-muted-2',
}

function formatWhyMovingDate(dateTime: string): string {
  const date = new Date(dateTime)
  if (isToday(date)) {
    return `Today, ${format(date, 'h:mm a')}`
  }

  return format(date, 'MMM dd, h:mm a')
}

function WhyStockMovingComponent() {
  const { index, ticker } = React.useContext(QuoteContext)

  const initialData = React.useMemo(
    () =>
      parseRouteInitData<WhyStockMovingInitData>({
        elementId: `why-stock-moving-init-data-${index}`,
        removeElement: false,
      }),
    [index]
  )

  // Fetch only the whyMoving data (headline + message) on refresh
  const { data: whyMoving } = useWhyStockMoving(ticker, initialData?.whyMoving)

  const [seeMore, setSeeMore] = React.useState(false)
  const feedbackDialog = useDialogState()

  // Use initial ratings from server-rendered data - ratings are only updated via user action
  const { liked, doRating } = useRating(initialData?.whyMovingRatings?.liked ?? null, feedbackDialog.show)

  if (!whyMoving) {
    return null
  }

  return (
    <div className="fv-container mb-2 flex items-center">
      <div
        className={classnames('relative flex w-full flex-col border border-primary bg-primary p-1.5 text-sm', {
          'rounded-md': FinvizSettings.hasRedesignEnabled,
        })}
      >
        <div className="flex items-start justify-between">
          <div className="inline-block pr-5 leading-relaxed">
            <span className="relative -top-px pr-1">
              <WithTooltip text="AI-generated content may be incorrect">
                <span>
                  <Icon name="aiGenerated" width={16} className="!inline-block text-muted-2" />
                </span>
              </WithTooltip>
            </span>
            <span className={classnames(SENTIMENT_COLORS_CLASSNAMES[whyMoving.sentiment], 'whitespace-nowrap pr-2')}>
              {formatWhyMovingDate(whyMoving.dateTime)}
            </span>
            <span>
              {whyMoving.headline}{' '}
              {!seeMore && whyMoving.summary && <ToggleButton seeMore={seeMore} setSeeMore={setSeeMore} />}
            </span>
          </div>
          <div className="mr-1 mt-[0.336rem] flex h-full items-center gap-3">
            <LikeButton liked={liked} onClick={() => doRating(whyMoving.id, true)} isThumbsUp={true} />
            <LikeButton liked={liked} onClick={() => doRating(whyMoving.id, false)} isThumbsUp={false} />
          </div>
        </div>
        {whyMoving.summary && seeMore && (
          <div className="mt-2 flex">
            <span className="text-muted-2">
              {whyMoving.summary} <ToggleButton seeMore={seeMore} setSeeMore={setSeeMore} />
            </span>
          </div>
        )}
      </div>
      <FeedbackDialog state={feedbackDialog} doRating={doRating} stockWhyMovingHistoryId={whyMoving.id} />
    </div>
  )
}

export const WhyStockMoving = withQueryClientContext(WhyStockMovingComponent)
